/**
 * AI Status Store — issues #146 + #824.
 *
 * Manages the AI availability status. Polls the server every 60s
 * (when the browser tab is visible) to detect mode transitions
 * between "AI Enhanced" and "Basic Mode". When a transition is
 * detected, `onTransition` callbacks fire so the UI can show a toast.
 */

import { create } from 'zustand';
import { api, type AIStatus } from '../services/api';

const POLL_INTERVAL_MS = 60_000;
const CACHE_TTL_MS = 5 * 60 * 1000;

type TransitionCallback = (
  newMode: 'ai-enhanced' | 'basic',
  status: AIStatus,
) => void;

interface AIStatusState {
  status: AIStatus | null;
  isLoading: boolean;
  error: string | null;
  lastChecked: Date | null;
  fetchStatus: () => Promise<void>;
  forceRefresh: () => Promise<void>;
  invalidateCache: () => void;
  startPolling: () => void;
  stopPolling: () => void;
  onTransition: (cb: TransitionCallback) => () => void;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
const transitionCallbacks = new Set<TransitionCallback>();

export const useAIStatusStore = create<AIStatusState>((set, get) => ({
  status: null,
  isLoading: false,
  error: null,
  lastChecked: null,

  fetchStatus: async () => {
    const lastChecked = get().lastChecked;
    if (lastChecked && Date.now() - lastChecked.getTime() < CACHE_TTL_MS) {
      return;
    }
    await fetchAndUpdate(set, get);
  },

  forceRefresh: async () => {
    set({ lastChecked: null });
    await fetchAndUpdate(set, get);
  },

  invalidateCache: () => {
    set({ lastChecked: null });
  },

  /**
   * Issue #824: start polling for mode transitions. Idempotent.
   * Only polls when the document is visible to avoid battery drain.
   */
  startPolling: () => {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      void fetchAndUpdate(set, get);
    }, POLL_INTERVAL_MS);
  },

  stopPolling: () => {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },

  /**
   * Register a callback for mode transitions. Returns an unsubscribe
   * function. Used by the Header to fire a toast.
   */
  onTransition: (cb: TransitionCallback) => {
    transitionCallbacks.add(cb);
    return () => transitionCallbacks.delete(cb);
  },
}));

async function fetchAndUpdate(
  set: (partial: Partial<AIStatusState>) => void,
  get: () => AIStatusState,
) {
  set({ isLoading: true, error: null });
  try {
    const newStatus = await api.getAIStatus();
    const oldStatus = get().status;

    // Detect transition.
    const oldMode = oldStatus?.mode ?? (oldStatus?.available ? 'ai-enhanced' : 'basic');
    const newMode = newStatus.mode ?? (newStatus.available ? 'ai-enhanced' : 'basic');
    if (oldStatus && oldMode !== newMode) {
      for (const cb of transitionCallbacks) {
        try {
          cb(newMode, newStatus);
        } catch {
          // Don't let a bad callback break polling.
        }
      }
    }

    set({
      status: newStatus,
      isLoading: false,
      lastChecked: new Date(),
    });
  } catch (err) {
    set({
      error: err instanceof Error ? err.message : 'Failed to fetch AI status',
      isLoading: false,
      lastChecked: new Date(),
    });
  }
}
